<template>
  <div class="dashboard-container">
    <!-- Côté interne, le widget Bienvenue de l'accueil tient ce rôle : le
         bandeau ne reste que pour le client extranet, qui n'a pas de widgets. -->
    <div
      v-if="estExtranet"
      class="welcome-banner"
    >
      <div class="welcome-left">
        <h2 class="welcome-title">
          Bonjour {{ userFirstName }}
        </h2>
        <p class="welcome-subtitle">
          {{ todayFormatted }}
          <el-tag
            size="small"
            effect="plain"
            class="welcome-role-tag"
          >
            {{ userRoles }}
          </el-tag>
        </p>
      </div>
      <el-button
        text
        @click="$router.push('/profile/edit')"
      >
        <MdiIcon
          name="AccountEdit"
          :size="18"
        />
        <span style="margin-left: 6px;">Mon profil</span>
      </el-button>
    </div>

    <!-- L'accueil par widgets remplace l'encart des tâches et la grille de
         tuiles : les deux sont devenus des widgets, disposés par chacun. Les
         quatre appels du tableau de bord sont réservés au périmètre interne, on
         ne les lance donc pas pour un client extranet. -->
    <AccueilWidgets v-if="!estExtranet" />

    <!-- Widget: Extranet - pour les clients extranet -->
    <el-card
      v-if="estExtranet"
      class="dashboard-card extranet-dashboard-card"
    >
      <template #header>
        <div class="card-header">
          <el-icon><LinkIcon /></el-icon>
          <span>Mes accès extranet</span>
          <el-tag
            v-if="extranetAccounts.length > 1"
            type="info"
            size="small"
            style="margin-left: auto;"
          >
            {{ extranetAccounts.length }} comptes
          </el-tag>
        </div>
      </template>

      <!-- Chargement -->
      <div
        v-if="loadingExtranet"
        v-loading="true"
        class="extranet-loading"
      />

      <!-- Vue multi-comptes -->
      <div
        v-else-if="extranetAccounts.length > 1"
        class="extranet-accounts"
      >
        <el-row :gutter="16">
          <el-col
            v-for="account in extranetAccounts"
            :key="account.id"
            :xs="24"
            :sm="12"
            :md="8"
            :lg="6"
          >
            <el-card
              class="account-card"
              shadow="hover"
              @click="goToExtranetAccount(account)"
            >
              <div class="account-card-content">
                <div class="account-icon">
                  <el-icon :size="32">
                    <OfficeBuilding v-if="account.is_group" />
                    <User v-else />
                  </el-icon>
                </div>
                <div class="account-info">
                  <div class="account-name">
                    {{ account.nom_aff || account.name }}
                  </div>
                  <div
                    v-if="account.is_group"
                    class="account-type"
                  >
                    <el-tag
                      size="small"
                      type="success"
                    >
                      Groupe
                    </el-tag>
                  </div>
                </div>
                <el-icon class="account-arrow">
                  <ArrowRight />
                </el-icon>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </div>

      <!-- Compte unique - redirection automatique -->
      <div
        v-else-if="extranetAccounts.length === 1"
        class="extranet-info"
      >
        <el-alert
          title="Redirection automatique"
          type="info"
          :description="'Vous serez automatiquement redirigé vers ' + (extranetAccounts[0]?.nom_aff || 'votre espace extranet') + '.'"
          :closable="false"
          show-icon
        />
      </div>

      <!-- Aucun compte -->
      <div
        v-else
        class="extranet-info"
      >
        <el-alert
          title="Aucun accès"
          type="warning"
          description="Vous n'avez accès à aucun compte extranet pour le moment."
          :closable="false"
          show-icon
        />
      </div>
    </el-card>

    <div
      v-if="$node_env != 'production'"
      class="dev-notice"
    >
      <el-tag
        type="warning"
        size="large"
      >
        Version dev/preprod
      </el-tag>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import {
  User, UserFilled, CreditCard, Avatar, Edit,
  Setting, Lock, DataAnalysis, TrendCharts, DataLine,
  Phone, Link as LinkIcon, Promotion,
  OfficeBuilding, ArrowRight
} from '@element-plus/icons-vue';
import { markRaw } from 'vue';
import { mapGetters } from 'vuex';
import AccueilWidgets from "@/views/dashboard/AccueilWidgets.vue";

export default {
  name: "HomeView",
  components: {
    User, UserFilled, CreditCard, Avatar, Edit,
    Setting, Lock, DataAnalysis, TrendCharts, DataLine,
    Phone, LinkIcon, Promotion,
    OfficeBuilding, ArrowRight,
    AccueilWidgets
  },
  data() {
    return {
      // Icons (markRaw pour éviter le warning Vue)
      User: markRaw(User),
      UserFilled: markRaw(UserFilled),
      CreditCard: markRaw(CreditCard),
      Edit: markRaw(Edit),
      Lock: markRaw(Lock),
      TrendCharts: markRaw(TrendCharts),
      DataLine: markRaw(DataLine),
      Phone: markRaw(Phone),
      // Extranet multi-comptes
      loadingExtranet: false,
      extranetAccounts: [],
    };
  },
  computed: {
    ...mapGetters({
      roles: 'user/roles',
      permissions: 'user/permissions'
    }),
    userFirstName() {
      const user = this.$store.state.user;
      if (user.prenom) {
        return user.prenom.charAt(0).toUpperCase() + user.prenom.slice(1);
      }
      return user.name || 'Utilisateur';
    },
    userName() {
      const user = this.$store.state.user;
      const name = user.name ? user.name.toUpperCase() : '';
      const prenom = user.prenom ? user.prenom.charAt(0).toUpperCase() + user.prenom.slice(1) : '';
      return `${name} ${prenom}`.trim() || 'Utilisateur';
    },
    userRoles() {
      return this.roles.length > 0 ? this.roles.join(', ') : 'Aucun rôle';
    },
    /**
     * Un client extranet n'a pas d'accueil par widgets : le serveur refuse les
     * quatre routes du tableau de bord à tout rôle de catégorie `extranet`.
     *
     * `$isExtranet()` et pas un nom de rôle : `Acl` déclare CINQ profils extranet
     * — client, consultation, facturation, exploitation, administrateur — et
     * tester le seul premier classait les quatre autres du côté interne. Le
     * client ainsi mal rangé se voyait monter l'accueil interne, récoltait quatre
     * 403, et perdait au passage la liste de ses comptes. C'est exactement le
     * défaut que `ca8a5381b` a fermé en faisant calculer le côté par le serveur
     * (`UserResource.scope`, dérivé de la catégorie du rôle) ; le repli sur
     * l'ancien test par nom vit dans le magasin, pas ici.
     */
    estExtranet() {
      return this.$isExtranet();
    },
    todayFormatted() {
      return new Date().toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    },
  },
  created() {
    if (this.estExtranet) {
      this.loadExtranetAccounts();
    }
  },
  methods: {
    loadExtranetAccounts() {
      const extranet_routes = this.$store.state.user.extranet_routes || [];

      this.extranetAccounts = extranet_routes.map(route => ({
        id: route.id,
        nom_aff: route.nom_aff || route.name || `Compte ${route.id}`,
        is_group: route.is_group || false
      }));
    },
    goToExtranetAccount(account) {
      if (account.is_group) {
        this.$router.push(`/extranet/group/${account.id}`);
      } else {
        this.$router.push(`/extranet/dash/${account.id}`);
      }
    },
  },
  watch: {
    $router: {
      handler: function(routes) {
        if (this.estExtranet){
          const allRoutes = routes.getRoutes();
          const extranet_routes = this.$store.state.user.extranet_routes || [];

          if (extranet_routes.length > 1) {
            return;
          }

          var extranet_grp_routes = [];
          var extranet_cli_routes = [];

          extranet_routes.forEach(element => {
            if (element.is_group){
              extranet_grp_routes.push(element);
            } else {
              extranet_cli_routes.push(element);
            }
          });

          if (extranet_grp_routes.length > 0){
            const baseGrpRoute = '/extranet/group/' ;
            const route2Go     = baseGrpRoute + extranet_grp_routes[0].id;
            const routeToCheck = baseGrpRoute + ":id_grp(\\d+)";
            const routeExists  = allRoutes.some(route => route.path === routeToCheck);

            if (routeExists) {
              this.$router.push(route2Go);
            }
            return;

          } else if (extranet_cli_routes.length > 0){
            const baseCliRoute = '/extranet/dash/' ;
            const route2Go     = baseCliRoute + extranet_cli_routes[0].id;
            const routeToCheck = baseCliRoute + ":id(\\d+)";
            const routeExists  = allRoutes.some(route => route.path === routeToCheck);

            if (routeExists) {
              this.$router.push(route2Go);
            }
            return;
          }
        }
      },
      immediate: true
    }
  }
};
</script>

<style scoped>
.dashboard-container {
  padding: 20px;
  min-height: calc(100vh - 100px);
}

/* ========================================
   Bandeau de bienvenue
   ======================================== */
.welcome-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #02264a 0%, #3a5a7a 100%);
  border-radius: 12px;
  color: white;
}

.welcome-title {
  margin: 0;
  font-size: 1.5em;
  font-weight: 600;
}

.welcome-subtitle {
  margin: 6px 0 0;
  font-size: 0.9em;
  opacity: 0.85;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.welcome-role-tag {
  --el-tag-bg-color: rgba(255, 255, 255, 0.15);
  --el-tag-border-color: rgba(255, 255, 255, 0.3);
  --el-tag-text-color: #ffffff;
}

.welcome-banner .el-button {
  color: white;
  --el-button-text-color: white;
  --el-button-hover-text-color: #6ed6ce;
}

/* ========================================
   Dashboard cards (extranet)
   ======================================== */
.dashboard-card {
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: bold;
  color: #02264A;
}

.card-header .el-icon {
  font-size: 20px;
}

.extranet-info {
  padding: 10px 0;
}

.dev-notice {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

/* ========================================
   Extranet Multi-Comptes
   ======================================== */
.extranet-dashboard-card {
  margin-bottom: 20px;
}

.extranet-loading {
  min-height: 100px;
}

.extranet-accounts {
  padding: 10px 0;
}

.account-card {
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 16px;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}

.account-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  border-color: #02264a;
}

.account-card :deep(.el-card__body) {
  padding: 16px;
}

.account-card-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.account-icon {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  background: linear-gradient(135deg, #02264a, #3a5a7a);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.account-info {
  flex: 1;
  min-width: 0;
}

.account-name {
  font-weight: 600;
  font-size: 1em;
  color: #303133;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.account-type {
  margin-top: 4px;
}

.account-arrow {
  color: #909399;
  transition: transform 0.3s ease;
}

.account-card:hover .account-arrow {
  transform: translateX(4px);
  color: #02264a;
}

/* Responsive */
@media (max-width: 768px) {
  .dashboard-container {
    padding: 10px;
  }

  .welcome-banner {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .welcome-title {
    font-size: 1.2em;
  }

  .account-card-content {
    gap: 8px;
  }

  .account-icon {
    width: 40px;
    height: 40px;
  }

  .account-name {
    font-size: 0.9em;
  }
}
</style>

<style>
/* ========================================
   Dark Mode Styles
   ======================================== */
html.dark .dashboard-container {
  background-color: #0d0d0d;
}

/* Welcome banner dark mode */
html.dark .welcome-banner {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
}

/* Dashboard card dark mode */
html.dark .dashboard-card {
  background-color: #1a1a1a;
  border-color: #2a2a2a;
}

html.dark .dashboard-card:hover {
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.4);
}

html.dark .card-header {
  color: #6ed6ce !important;
  font-weight: 600;
}

html.dark .card-header span {
  color: #6ed6ce !important;
}

html.dark .card-header .el-icon {
  color: #6ed6ce !important;
}

/* Account cards dark mode */
html.dark .account-card {
  background-color: #1a1a1a;
  border-color: #3a3a3a;
}

html.dark .account-card:hover {
  border-color: #6ed6ce;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
}

html.dark .account-name {
  color: #e0e0e0;
}

html.dark .account-arrow {
  color: #6a6a6a;
}

html.dark .account-card:hover .account-arrow {
  color: #6ed6ce;
}

</style>
